import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

export const SCORE_POLL_INTERVAL_MS = 10_000;

interface UseScoreArrivalFallbackParams {
  propertyId: string | undefined;
  /** True while the uploader's WebSocket-driven progress says the report is still processing. */
  isUploadProcessing: boolean;
  conditionScore: number | null;
  isEqProcessing: boolean;
  /** Called once when a score lands that wasn't there when processing started. */
  onScoreArrived: () => void;
}

/**
 * Onboarding's "Building your Home HQ" step ends when the AI pipeline's final
 * WebSocket message arrives. If that message is dropped (flaky socket,
 * reconnect, local `sst dev`), the step would spin forever even though the
 * report was processed and scored. Hauser-EQ only scores after the pipeline
 * finishes, so a score appearing that wasn't there at the start is a reliable
 * second "done" signal. Polls for it while processing and reports its arrival.
 */
export const useScoreArrivalFallback = ({
  propertyId,
  isUploadProcessing,
  conditionScore,
  isEqProcessing,
  onScoreArrived,
}: UseScoreArrivalFallbackParams) => {
  const queryClient = useQueryClient();
  // undefined = not processing; otherwise the score seen when processing began.
  const scoreAtStartRef = useRef<number | null | undefined>(undefined);
  const onScoreArrivedRef = useRef(onScoreArrived);
  onScoreArrivedRef.current = onScoreArrived;

  useEffect(() => {
    if (!isUploadProcessing) {
      scoreAtStartRef.current = undefined;
      return;
    }
    if (scoreAtStartRef.current === undefined) {
      scoreAtStartRef.current = conditionScore;
      return;
    }
    if (scoreAtStartRef.current === null && conditionScore != null && !isEqProcessing) {
      onScoreArrivedRef.current();
    }
  }, [isUploadProcessing, conditionScore, isEqProcessing]);

  // The Hauser-EQ query stops polling once it has no in-flight evaluation, so
  // nudge it while the upload is processing or a newly created score is never fetched.
  useEffect(() => {
    if (!isUploadProcessing || !propertyId) return;
    const intervalId = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['hauser-eq-results', propertyId] });
    }, SCORE_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isUploadProcessing, propertyId, queryClient]);
};
